import React, { useMemo } from 'react';
import { Box, FormControl, InputLabel, Select, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { DEEP_GREEN, INCIDENT_ACCENT, INK, MUTED, NEUTRAL_SURFACE, WARN_ORANGE } from '../colorTokens';
import { formatUtcDate, IMPACT_NOTE, MAJOR_DEFINITION, openSummary, PRIORITY_NOTE, problemNote } from './reportCopy';

// The visible sections of the monthly report, in the order the page shows them. They live in one file
// because each is used once, by MonthlyReport, and none is useful on its own.

// ---------- shared ----------

/** Renders the { text, bold, color } parts the report builds its sentences from. */
const TextParts = ({ parts }) => (
  <>
    {parts.map((part, index) => (
      <Box component="span" key={`${index}-${part.text}`} sx={{ fontWeight: part.bold ? 700 : 500, color: part.color }}>
        {part.text}
      </Box>
    ))}
  </>
);

// ---------- month picker ----------

// The real <Select> drives the value/a11y/keyboard behavior as normal, but its own
// text is made transparent and a separate Box overlays the visible label -- native
// <option> text can't be partially bold, so this is how "— Open" gets bolded.
// Don't remove `color: 'transparent'` without removing the overlay Box too, or the
// label will render twice.
export const MonthPicker = ({ groups, activeKey, active, onChange }) => (
  <FormControl size="small" sx={{ width: 250, position: 'relative' }}>
    <InputLabel>Month</InputLabel>
    <Select
      native
      label="Month"
      value={activeKey}
      onChange={(event) => onChange(event.target.value)}
      sx={{ color: 'transparent', caretColor: INK, '& option': { color: INK } }}
    >
      {groups.map((group) => (
        <option key={group.key} value={group.key}>{group.label}{group.attention ? ' - Open' : ''}</option>
      ))}
    </Select>
    <Box sx={{ position: 'absolute', left: 14, right: 32, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '0.875rem', color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {active.label}
      {active.attention && (
        <>
          {' - '}
          <Box component="span" sx={{ fontWeight: 700 }}>Open</Box>
        </>
      )}
    </Box>
  </FormControl>
);

/** The one-sentence read of the open problems, above the chart. */
export const OpenSummary = ({ facts }) => (
  <Typography sx={{ fontSize: '1rem', lineHeight: 1.5, color: INK, mb: 2 }}>
    <TextParts parts={openSummary(facts).parts} />
  </Typography>
);

/** What Major and Non-major mean, under the chart. */
export const MajorDefinition = () => (
  <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.45, color: INK, mt: 1.25, mb: 2 }}>
    <TextParts parts={MAJOR_DEFINITION.parts} />
  </Typography>
);

// ---------- summary tiles ----------

const NON_MAJOR_TONE = '#2C4A3A';

const FlowNode = ({ value, label, tone, compact = false }) => (
  <Box sx={{ width: 'fit-content', minWidth: compact ? 58 : 108, px: compact ? 1 : 1.5, py: compact ? 0.65 : 1, borderRadius: '8px', bgcolor: tone, textAlign: 'center' }}>
    <Typography sx={{ fontSize: compact ? '1rem' : '1.35rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>{value}</Typography>
    <Typography sx={{ fontSize: compact ? '0.6rem' : '0.68rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.82)', mt: 0.35 }}>{label}</Typography>
  </Box>
);

const FlowArrow = () => (
  <Box aria-hidden sx={{ color: MUTED, fontSize: '1.35rem', lineHeight: 1, px: 0.25 }}>→</Box>
);

const FlowSplit = ({ count, name, tone, closed, open, months }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
    <FlowNode value={count} label={name} tone={tone} />
    <FlowArrow />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      <FlowNode value={closed} label="Closed" tone={MUTED} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <FlowNode value={open} label="Open" tone={open ? INCIDENT_ACCENT : DEEP_GREEN} />
        {months.length > 0 && (
          <>
            <FlowArrow />
            {months.map((month) => (
              <FlowNode key={month.key} compact value={month.count} label={month.label} tone={INCIDENT_ACCENT} />
            ))}
          </>
        )}
      </Box>
    </Box>
  </Box>
);

/** Total problems up to the chosen month, split into major / non-major and closed / still open. */
export const FlowSummary = ({ scope, active }) => {
  const closedMonthLabel = active.shortLabel.split(' ')[0];
  return (
    <Box sx={{ flex: '1.4 1 560px', minWidth: 320 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <FlowNode value={scope.total} label={`Through ${active.shortLabel}`} tone={DEEP_GREEN} />
        <FlowArrow />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <FlowSplit
            count={scope.major}
            name="Major"
            tone={DEEP_GREEN}
            closed={scope.majorClosed}
            open={scope.majorOpen}
            months={scope.majorOpenMonths}
          />
          <FlowSplit
            count={scope.nonMajor}
            name="Non-major"
            tone={NON_MAJOR_TONE}
            closed={scope.nonMajorClosed}
            open={scope.nonMajorOpen}
            months={scope.nonMajorOpenMonths}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, flexWrap: 'wrap', mt: 1.5 }}>
        <FlowNode value={active.closedThisMonth || 0} label={`Closed in ${closedMonthLabel}`} tone={MUTED} />
        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: MUTED, maxWidth: 320, pt: 0.35 }}>
          How many problems closed this month, no matter when they opened. Under Major / Non-major, Closed means
          opened in the period and now closed.
        </Typography>
      </Box>
    </Box>
  );
};

// ---------- trend chart ----------

const barTotal = (row, props, fill) => {
  if (!row.opened) return <g />;
  const { x, y, width } = props;
  return (
    <text x={x + width / 2} y={y - 4} textAnchor="middle" fill={fill} fontSize={10} fontWeight={700}>
      {row.opened}
    </text>
  );
};

const closedMonthTotal = (chart) => (props) => {
  const row = chart[props.index] || {};
  if (row.openProblems) return <g />;
  return barTotal(row, props, DEEP_GREEN);
};

const openMonthTotal = (chart) => (props) => {
  const row = chart[props.index] || {};
  if (!row.openProblems) return <g />;
  return barTotal(row, props, row.closed > 0 ? INK : INCIDENT_ACCENT);
};

const openSegmentCount = (chart) => (props) => {
  const row = chart[props.index] || {};
  if (!(row.openProblems > 0 && row.closed > 0) || !props.height || props.height < 12) return <g />;
  const { x, y, width, height } = props;
  return (
    <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize={10} fontWeight={700}>
      {row.openProblems}
    </text>
  );
};

const LegendKey = ({ color, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
    <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>{text}</Typography>
  </Box>
);

/** Problems opened per month (closed vs still open), with the average time to close and the trend sentence. */
export const TrendChart = ({ scope, averageClosedDays, closedForAverageCount, closedThisMonth, monthLabel }) => {
  // Recharts re-creates its labels when the render function changes, so build them once per chart.
  const labels = useMemo(() => ({
    closedTotal: closedMonthTotal(scope.chart),
    openTotal: openMonthTotal(scope.chart),
    openSegment: openSegmentCount(scope.chart)
  }), [scope.chart]);

  return (
    <Box sx={{ flex: '1 1 340px', minWidth: 280 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: INK }}>
          Trends
        </Typography>
        <LegendKey color={MUTED} text="Opened that month, later closed" />
        <LegendKey color={INCIDENT_ACCENT} text="Opened that month, still open" />
      </Box>
      <Box sx={{ width: '100%', height: 188 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={scope.chart} margin={{ top: 18, right: 4, left: 0, bottom: 0 }} barCategoryGap="32%">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,46,34,0.08)" vertical={false} />
            <XAxis dataKey="tick" interval={0} tick={{ fontSize: 10, fill: MUTED }} height={22} />
            <YAxis tick={{ fontSize: 10, fill: MUTED }} allowDecimals={false} width={28} />
            <Bar dataKey="closed" name="Opened that month, later closed" stackId="month" fill={MUTED} maxBarSize={18}>
              <LabelList dataKey="opened" content={labels.closedTotal} />
            </Bar>
            <Bar dataKey="openProblems" name="Opened that month, still open" stackId="month" fill={INCIDENT_ACCENT} maxBarSize={18}>
              <LabelList dataKey="opened" content={labels.openTotal} />
              <LabelList dataKey="openProblems" content={labels.openSegment} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      {closedThisMonth != null && (
        <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.45, mt: 0.75, color: INK }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{closedThisMonth}</Box>
          {` problem${closedThisMonth === 1 ? ' was' : 's were'} closed in ${monthLabel}.`}
        </Typography>
      )}
      {averageClosedDays != null && (
        <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.45, mt: 0.75, color: INK }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{averageClosedDays}</Box>
          {` days is the average time to close, across ${closedForAverageCount} closed problem${closedForAverageCount === 1 ? '' : 's'}.`}
        </Typography>
      )}
      {scope.trend.parts.length > 0 && (
        <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.45, mt: 0.75, color: INK }}>
          <TextParts parts={scope.trend.parts} />
        </Typography>
      )}
    </Box>
  );
};

// ---------- month details ----------

const Section = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: INK, mb: 0.75 }}>{title}</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{children}</Box>
  </Box>
);

/** One open problem: its number and title, then a labelled row per line of the note. */
const ProblemNote = ({ fact }) => {
  const note = problemNote(fact);
  return (
    <Box component="li" sx={{ fontSize: '0.95rem', lineHeight: 1.45, mb: 1.25, color: INK }}>
      <Box component="span" sx={{ fontWeight: 700, color: INCIDENT_ACCENT }}>{note.number}</Box>
      {note.lines.map((line) => (
        <Box key={line.label} sx={{ mt: 0.25 }}>
          <Box component="span" sx={{ fontWeight: 700, color: line.color }}>{`${line.label}: `}</Box>
          {line.text}
        </Box>
      ))}
    </Box>
  );
};

/** The chosen month in words, then every problem still open up to that month. */
export const MonthDetails = ({ active, stillOpenFacts }) => (
  <>
    <Section title={active.label}>
      {active.points.map((point, pointIndex) => (
        <Box component="li" key={`${pointIndex}-${point.parts.map((part) => part.text).join('')}`} sx={{ fontSize: '0.95rem', lineHeight: 1.45, mb: 0.35, color: INK }}>
          <TextParts parts={point.parts} />
        </Box>
      ))}
    </Section>

    {stillOpenFacts.length > 0 && (
      <Section title="Still open: status and next steps">
        {stillOpenFacts.map((fact) => <ProblemNote key={fact.number} fact={fact} />)}
      </Section>
    )}
  </>
);

// ---------- problem table ----------

const RCA_COLUMN = 11;

export const COLUMNS = [
  'Number', 'Incident', 'Priority', 'Opened', 'Closed', 'Assignment group', 'Impact (hours)',
  'Impact (days)', 'Closed (days)', 'Problem statement', 'RCA', 'Class', 'State'
];

export const withUnit = (value, unit) => {
  if (value == null || value === '') return '';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '';
  const shown = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${shown} ${unit}`;
};

export const days = (value) => withUnit(value, value === 1 ? 'day' : 'days');

const stat = { color: INCIDENT_ACCENT, fontWeight: 700 };

/** One row per problem for the chosen month (including earlier months' problems that are still open). */
export const ProblemTable = ({ rows }) => {
  if (!rows.length) {
    return (
      <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>
        Nothing is still open.
      </Typography>
    );
  }
  return (
    <>
      <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.45, fontWeight: 700, color: INK, mt: 2 }}>{PRIORITY_NOTE}</Typography>
      <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.45, color: MUTED, mt: 0.5 }}>
        <TextParts parts={IMPACT_NOTE.parts} />
      </Typography>
      <Box sx={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid rgba(26,46,34,0.12)', mt: 1 }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: 1400,
            '& th': { textAlign: 'left', bgcolor: DEEP_GREEN, color: '#fff', fontSize: '0.75rem', fontWeight: 600, px: 1.5, py: 1 },
            '& td': { px: 1.5, py: 1, fontSize: '0.8125rem', color: INK, borderBottom: '1px solid rgba(26,46,34,0.08)', verticalAlign: 'top' },
            [`& th:nth-of-type(${RCA_COLUMN}), & td:nth-of-type(${RCA_COLUMN})`]: {
              minWidth: 360,
              maxWidth: 480,
              whiteSpace: 'normal',
              wordBreak: 'normal',
              overflowWrap: 'break-word',
              lineHeight: 1.45
            },
            '& tbody tr:nth-of-type(even) td': { bgcolor: NEUTRAL_SURFACE }
          }}
        >
          <thead>
            <tr>{COLUMNS.map((column) => <th key={column}>{column}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.problemNumber}>
                <td style={{ fontWeight: 700 }}>{row.problemNumber}</td>
                <td style={{ fontWeight: 700 }}>{row.incidentNumbers}</td>
                <td style={{ color: DEEP_GREEN, fontWeight: 700 }}>{row.priority}</td>
                <td>{formatUtcDate(row.openedAt)}</td>
                <td>{formatUtcDate(row.problemClosedAt)}</td>
                <td>{row.teamName}</td>
                <td style={stat}>{withUnit(row.impactHours, 'hrs')}</td>
                <td style={stat}>{days(row.impactDays)}</td>
                <td style={stat}>{days(row.closedDays)}</td>
                <td>{row.problemShortDescription}</td>
                <td>{row.problemRca}</td>
                <td style={{ color: row.major ? INCIDENT_ACCENT : DEEP_GREEN, fontWeight: 700 }}>{row.classLabel}</td>
                <td style={{ color: row.isOpen ? WARN_ORANGE : INK, fontWeight: row.isOpen ? 700 : 500 }}>{row.problemState}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </>
  );
};
